import {useQuery} from '@apollo/client'
import {Button, Typography} from '@mui/material'
import {makeStyles} from '@mui/styles'
import gql from 'graphql-tag'
import {useTranslate} from 'react-admin'

import useHasPermission from '../../hooks/useHasPermission'
import useIsOwner from '../../hooks/useIsOwner'
import {QueryRoot} from '../../types/graphqlSchema'
import env from '../../utils/env'
import {authenticate as authenticateSalesforce} from '../../utils/salesforce'
import {HubspotIcon, MicrosoftDynamicsIcon, SalesforceIcon, SapIcon} from '../icons'

const ThirdPartyConnections = () => {
  const styles = useStyles()
  const translate = useTranslate()
  const isOwner = useIsOwner()
  /* TODO: On mobile we need to load the page in a nested webview and then
            intercept the redirect back to the app to close the webview. */
  const canAuthenticateSalesforce = useHasPermission('edit', 'merchant_users')
  const {
    data: {merchants: [{salesforceConnectionStatus = 'CONNECTED'} = {}] = []} = {},
  } = useQuery<QueryRoot['merchants']>(MERCHANTS_QUERY)
  const isConnectedToSalesforce = salesforceConnectionStatus === 'CONNECTED'
  if (!canAuthenticateSalesforce) return null
  return (<>
    <Typography className={styles.thirdPartyConnectionsTitle} variant="subtitle2">
      {translate('dialogs.profile.merchantUsersSettings.thirdPartyConnections.title')}
    </Typography>
    <div className={styles.thirdPartyConnectionsButtons}>
      {canAuthenticateSalesforce && (
        <SalesforceConnectionButton
          disabled={isConnectedToSalesforce}
          isConnectedToSalesforce={isConnectedToSalesforce}
        />
      )}
      {isOwner && (
        <>
          <Button
            disabled
            startIcon={<HubspotIcon />}
            variant="outlined"
          >
            {translate(
              'dialogs.profile.merchantUsersSettings.thirdPartyConnections.' +
              'buttons.hubspot.synchronize'
            )}
          </Button>
          <Button
            disabled
            startIcon={<SapIcon />}
            variant="outlined"
          >
            {translate(
              'dialogs.profile.merchantUsersSettings.thirdPartyConnections.' +
              'buttons.sap.synchronize'
            )}
          </Button>
          <Button
            disabled
            startIcon={<MicrosoftDynamicsIcon />}
            variant="outlined"
          >
            {translate(
              'dialogs.profile.merchantUsersSettings.thirdPartyConnections.' +
              'buttons.microsoftDynamics.synchronize'
            )}
          </Button>
        </>
      )}
    </div>
  </>)
}

const SalesforceConnectionButton = ({disabled, isConnectedToSalesforce}) => (
  <Button
    disabled={disabled}
    onClick={() => authenticateSalesforce({
      clientId: SALESFORCE_CONNECTED_APP_CLIENT_ID,
      redirectPath: '/salesforce-authentication',
    })}
    startIcon={<SalesforceIcon />}
    variant="outlined"
  >
    {useTranslate()(
      'dialogs.profile.merchantUsersSettings.thirdPartyConnections.buttons.' +
        `${isConnectedToSalesforce ?
          'salesforce.synchronized' :
          'salesforce.synchronize'}`,
    )}
  </Button>
)


const MERCHANTS_QUERY = gql`query{
  merchants{id salesforceConnectionStatus}
}`

const useStyles = makeStyles(theme => ({
  thirdPartyConnectionsButtons: {
    // TODO: Move this style to the global theme
    '& .MuiButton-root': {
      [theme.breakpoints.down('md')]: {
        fontSize: theme.typography.button.fontSize,
      },
    },
  },
  thirdPartyConnectionsTitle: {
    padding: `${theme.remSpacing(1)} 0`,
  },
}))

const SALESFORCE_CONNECTED_APP_CLIENT_ID = env('SALESFORCE_CONNECTED_APP_CLIENT_ID')

export default ThirdPartyConnections
export {SalesforceConnectionButton}
